/**
 * Auth0 redirect configuration.
 *
 * Login callback:
 *   REACT_APP_REDIRECT_URI: full URL override
 *   REACT_APP_REDIRECT_PATH: path suffix (default /redirecting)
 *
 * Logout returnTo:
 *   REACT_APP_LOGOUT_REDIRECT_URI: full URL override
 *   REACT_APP_LOGOUT_REDIRECT_PATH: path suffix (default /)
 *
 * Multiple domains: both use current origin when env vars are unset.
 * Add each domain+path to Auth0 Allowed Callback URLs and Allowed Logout URLs.
 */
const rawPath = process.env.REACT_APP_REDIRECT_PATH || "/redirecting";
export const AUTH_REDIRECT_PATH = rawPath.startsWith("/")
  ? rawPath
  : `/${rawPath}`;

const rawLogoutPath =
  process.env.REACT_APP_LOGOUT_REDIRECT_PATH ?? "/";
export const LOGOUT_REDIRECT_PATH = rawLogoutPath.startsWith("/")
  ? rawLogoutPath
  : `/${rawLogoutPath}`;

function getOrigin(): string {
  return globalThis.location == null
    ? "http://localhost:3000"
    : globalThis.location.origin;
}

export function getAuthRedirectUri(): string {
  return `${getOrigin()}${AUTH_REDIRECT_PATH}`;
}

export function getLogoutRedirectUri(): string {
  return `${getOrigin()}${LOGOUT_REDIRECT_PATH}`;
}

/**
 * The post-login destination, only when it is a path inside this app; `/` otherwise.
 *
 * `?returnTo=` comes from the URL, so anyone can craft a login link that sets it.
 * React Router 6 hands `/\evil.example` to the browser unchanged, and the browser
 * reads a backslash as a slash, so it lands on `//evil.example`, another origin
 * (GHSA-wrjc-x8rr-h8h6; the fix exists only in React Router 7). The URL parser also
 * drops tabs and newlines, which turns `/\t/evil.example` into the same thing. So a
 * destination must start with exactly one `/` and carry no backslash or control
 * character.
 */
export function safeReturnTo(raw: unknown): string {
  if (typeof raw !== "string" || !raw.startsWith("/") || raw.startsWith("//")) {
    return "/";
  }
  for (let i = 0; i < raw.length; i += 1) {
    const code = raw.charCodeAt(i);
    if (code < 0x20 || code === 0x7f || code === 0x5c) {
      return "/";
    }
  }
  return raw;
}
